import {
  ClenchData,
  CombinedResults,
  HitBox,
} from "/@/shared/motion-menu-system/MenuTypes";

import { addEventListenerHashParamJson } from "@metapages/hash-query";
import { HashParamDefinition } from "@metapages/metapage";

import type {
  ControllerOptionsConfig,
  MotionController,
  MotionControllerParameters,
} from "./types.ts";

export interface TwoHandGrabInputs {
  clench?: ClenchData;
  reset?: boolean;
}

// === CONFIGURATION CONSTANTS ===

// Warmup & stillness
const WARMUP_DURATION_MS = 400; // Time fists must be still before activation
const STILLNESS_THRESHOLD = 0.0005; // Variance threshold (normalized coords)
const MIN_SAMPLES_FOR_STILLNESS = 10; // Min samples before checking stillness
const POSITION_HISTORY_SIZE = 20; // Sliding window size

// Distance thresholds for initial mode determination at warmup completion
const DISTANCE_MIN = 0.1; // Minimum expected distance
const DISTANCE_MAX = 0.7; // Maximum expected distance

// Visual
const GLOW_CIRCLE_SIZE = 80; // Fist circle diameter (px)
const PANEL_WIDTH_PERCENT = 65; // Each panel width - wide enough to always cover from edge to center+
// How much the panel edges should extend past the fist center (as fraction of viewport width).
// Panels close when fists are 2 × this apart — must be large enough that physical fists
// can reach the trigger. At 0.25 the panels close when fists are 50% of viewport apart.
const PANEL_FIST_OVERLAP = 0.25;
const PANEL_TRANSITION_DURATION = "0.3s";
const PANEL_TRANSITION_EASING = "ease-out";
const GLOW_PULSE_DURATION = "1.5s";
const FADE_IN_DURATION = "0.2s";
const SIGNAL_DISPLAY_DURATION_MS = 300; // How long to show signal indicator
const SIGNAL_FADE_DURATION = "0.5s"; // Fade out duration

// Zoom output
const ZOOMTHRESHOLD_TRIGGER_EVENT = 0.8;

// === OPTIONS ===

export const DefaultTwoHandGrabOpenCloseControllerOptions: Record<
  string,
  HashParamDefinition
> = {
  enable: {
    type: "boolean",
    default: { value: false },
    label: "Enable Two Hand Grab",
  },
  debug: {
    type: "boolean",
    default: { value: false },
    label: "Debug Display",
  },
  warmupDurationMs: {
    type: "number",
    default: { value: WARMUP_DURATION_MS },
    label: "Warmup Duration (ms)",
  },
  stillnessThreshold: {
    type: "number",
    default: { value: STILLNESS_THRESHOLD },
    label: "Stillness Threshold",
  },
  zoomThresholdTriggerEvent: {
    type: "number",
    default: { value: ZOOMTHRESHOLD_TRIGGER_EVENT },
    label: "Zoom Threshold Trigger",
  },
  panelFistOverlap: {
    type: "number",
    default: { value: PANEL_FIST_OVERLAP },
    label: "Panel Lead Past Fist (0–0.5)",
  },
};

export const optionsKey = "twoHandGrabOpenClose";
export const controllerOptionsConfig: ControllerOptionsConfig = {
  key: optionsKey,
  label: "Two Hand Grab Open/Close",
  optionDefinitions: DefaultTwoHandGrabOpenCloseControllerOptions,
};

export type TwoHandGrabOpenCloseOptions = {
  enable?: boolean;
  debug?: boolean;
  warmupDurationMs?: number;
  stillnessThreshold?: number;
  zoomThresholdTriggerEvent?: number;
  panelFistOverlap?: number;
};

// === TYPES ===

type GestureState = "idle" | "warmup" | "active_opening" | "active_closing";

interface PositionSample {
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
  timestamp: number;
}

// === Pure helpers ===

function normalizeAngleDelta(angle1: number, angle2: number): number {
  let delta = angle2 - angle1;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  return delta;
}

function calculateVariance(values: number[]): number {
  if (values.length === 0) return Infinity;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
  return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
}

function calculateNormalizedFistDistance(
  leftBox: HitBox,
  rightBox: HitBox,
): number {
  const leftCenterX = leftBox.x + leftBox.width / 2;
  const leftCenterY = leftBox.y + leftBox.height / 2;
  const rightCenterX = rightBox.x + rightBox.width / 2;
  const rightCenterY = rightBox.y + rightBox.height / 2;

  const dx = rightCenterX - leftCenterX;
  const dy = rightCenterY - leftCenterY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return Math.max(
    0,
    Math.min(1, (distance - DISTANCE_MIN) / (DISTANCE_MAX - DISTANCE_MIN)),
  );
}

function calculateCenterDistance(
  leftBox: { x: number; y: number; width: number; height: number },
  rightBox: { x: number; y: number; width: number; height: number },
): number {
  const leftCenterX = leftBox.x + leftBox.width / 2;
  const leftCenterY = leftBox.y + leftBox.height / 2;
  const rightCenterX = rightBox.x + rightBox.width / 2;
  const rightCenterY = rightBox.y + rightBox.height / 2;

  const dx = rightCenterX - leftCenterX;
  const dy = rightCenterY - leftCenterY;
  return Math.sqrt(dx * dx + dy * dy);
}

function shouldTriggerClose(
  leftFistX: number,
  rightFistX: number,
  panelFistOverlap: number,
): boolean {
  const currentGap = rightFistX - leftFistX;
  return currentGap <= 2 * panelFistOverlap;
}

// === Controller class ===

export class TwoHandGrabOpenCloseController implements MotionController {
  private options: TwoHandGrabOpenCloseOptions = {};
  private disposers: (() => void)[] = [];
  private params: MotionControllerParameters;

  // DOM elements
  private localRoot: HTMLDivElement;
  private visualizationContainer: HTMLDivElement;
  private leftPanel: HTMLDivElement;
  private rightPanel: HTMLDivElement;
  private leftFistCircle: HTMLDivElement;
  private rightFistCircle: HTMLDivElement;
  private debugOverlay: HTMLDivElement;
  private signalIndicator: HTMLDivElement;
  private styleSheet: HTMLStyleElement | null = null;

  // Zoom state
  private triggerEventSent: number | null = null;
  private initialDistance: number | null = null;
  private currentDistance: number | null = null;
  private currentZoom = 1.0;
  private zoomDelta = 0.0;
  private previousZoom = 1.0;
  private currentAngle: number | null = null;
  private previousAngle: number | null = null;
  private angleDelta: number | null = null;
  private currentViewportWidth = window.innerWidth;
  private currentViewportHeight = window.innerHeight;

  // Gesture state
  private gestureState: GestureState = "idle";
  private warmupStartTime: number | null = null;
  private positionHistory: PositionSample[] = [];

  // Closing gesture state
  private closingInitialLeftX: number | null = null;
  private closingInitialRightX: number | null = null;
  private closingTriggerDistance: number | null = null;

  // Opening gesture state
  private openingInitialLeftX: number | null = null;
  private openingInitialRightX: number | null = null;

  // Signal fade timeout
  private signalFadeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(params: MotionControllerParameters) {
    this.params = params;
    const { root: parentRoot } = params;

    if (parentRoot === undefined) {
      throw new Error("root is required");
    }
    if (!params.iostream) {
      throw new Error("iostream is required");
    }

    // Metaframe inputs are the primary source of options — apply them directly.
    const metaframeInputDisposer = params.iostream.onInputs(
      (inputs: Record<string, unknown>) => {
        if (optionsKey in inputs) {
          this.applyOptions(
            inputs[optionsKey] as TwoHandGrabOpenCloseOptions | undefined,
          );
        }
      },
    );
    if (metaframeInputDisposer) {
      this.disposers.push(metaframeInputDisposer);
    }

    // Hash params are a secondary source (e.g. URL-based config). Ignore the
    // undefined value the listener fires on registration so it never clobbers
    // options already delivered via metaframe inputs.
    this.disposers.push(
      addEventListenerHashParamJson(optionsKey, (options) => {
        if (options === undefined) {
          return;
        }
        this.applyOptions(options as TwoHandGrabOpenCloseOptions);
      }),
    );

    // === INJECT CSS KEYFRAMES ===
    const styleId = "two-hand-grab-styles";
    if (!document.getElementById(styleId)) {
      this.styleSheet = document.createElement("style");
      this.styleSheet.id = styleId;
      this.styleSheet.textContent = `
      @keyframes fist-glow-pulse {
        0%, 100% {
          box-shadow:
            0 0 20px 5px rgba(255, 255, 255, 0.8),
            0 0 40px 10px rgba(255, 255, 255, 0.4),
            0 0 60px 20px rgba(255, 255, 255, 0.2);
        }
        50% {
          box-shadow:
            0 0 30px 10px rgba(255, 255, 255, 0.9),
            0 0 50px 15px rgba(255, 255, 255, 0.5),
            0 0 70px 25px rgba(255, 255, 255, 0.3);
        }
      }
    `;
      document.head.appendChild(this.styleSheet);
    }

    // === CREATE ROOT CONTAINER ===
    this.localRoot = document.createElement("div");
    this.localRoot.style.opacity = "0";
    this.localRoot.style.visibility = "hidden";
    this.localRoot.style.transition = "opacity 0.3s, visibility 0.3s";
    this.localRoot.style.position = "absolute";
    this.localRoot.style.top = "0";
    this.localRoot.style.left = "0";
    this.localRoot.style.width = "100%";
    this.localRoot.style.height = "100%";
    this.localRoot.style.pointerEvents = "none";
    this.localRoot.style.zIndex = "1000";
    this.localRoot.style.display = "block";

    // === CREATE VISUALIZATION CONTAINER ===
    this.visualizationContainer = document.createElement("div");
    this.visualizationContainer.style.position = "absolute";
    this.visualizationContainer.style.top = "0";
    this.visualizationContainer.style.left = "0";
    this.visualizationContainer.style.width = "100%";
    this.visualizationContainer.style.height = "100%";
    this.visualizationContainer.style.overflow = "hidden";
    this.visualizationContainer.style.pointerEvents = "none";
    this.visualizationContainer.style.opacity = "0";
    this.visualizationContainer.style.transition =
      `opacity ${FADE_IN_DURATION} ease-in`;

    // === CREATE LEFT PANEL ===
    this.leftPanel = document.createElement("div");
    this.leftPanel.style.position = "absolute";
    this.leftPanel.style.top = "0";
    this.leftPanel.style.left = "0";
    this.leftPanel.style.height = "100%";
    this.leftPanel.style.width = `${PANEL_WIDTH_PERCENT}%`;
    this.leftPanel.style.backgroundColor = "rgba(30, 30, 30, 0.85)";
    this.leftPanel.style.transition =
      `transform ${PANEL_TRANSITION_DURATION} ${PANEL_TRANSITION_EASING}`;
    this.leftPanel.style.borderRight = "2px solid rgba(255, 255, 255, 0.3)";
    this.leftPanel.style.borderTopRightRadius = "8px";
    this.leftPanel.style.borderBottomRightRadius = "8px";
    this.leftPanel.style.transform = "translateX(-100%)";

    // === CREATE LEFT FIST CIRCLE ===
    this.leftFistCircle = document.createElement("div");
    this.leftFistCircle.style.position = "absolute";
    this.leftFistCircle.style.width = `${GLOW_CIRCLE_SIZE}px`;
    this.leftFistCircle.style.height = `${GLOW_CIRCLE_SIZE}px`;
    this.leftFistCircle.style.borderRadius = "50%";
    this.leftFistCircle.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
    this.leftFistCircle.style.border = "3px solid rgba(255, 255, 255, 0.9)";
    this.leftFistCircle.style.animation =
      `fist-glow-pulse ${GLOW_PULSE_DURATION} ease-in-out infinite`;
    this.leftFistCircle.style.transform = "translate(-50%, -50%)";
    this.leftFistCircle.style.pointerEvents = "none";
    this.leftPanel.appendChild(this.leftFistCircle);

    // === CREATE RIGHT PANEL ===
    this.rightPanel = document.createElement("div");
    this.rightPanel.style.position = "absolute";
    this.rightPanel.style.top = "0";
    this.rightPanel.style.right = "0";
    this.rightPanel.style.height = "100%";
    this.rightPanel.style.width = `${PANEL_WIDTH_PERCENT}%`;
    this.rightPanel.style.backgroundColor = "rgba(30, 30, 30, 0.85)";
    this.rightPanel.style.transition =
      `transform ${PANEL_TRANSITION_DURATION} ${PANEL_TRANSITION_EASING}`;
    this.rightPanel.style.borderLeft = "2px solid rgba(255, 255, 255, 0.3)";
    this.rightPanel.style.borderTopLeftRadius = "8px";
    this.rightPanel.style.borderBottomLeftRadius = "8px";
    this.rightPanel.style.transform = "translateX(100%)";

    // === CREATE RIGHT FIST CIRCLE ===
    this.rightFistCircle = document.createElement("div");
    this.rightFistCircle.style.position = "absolute";
    this.rightFistCircle.style.width = `${GLOW_CIRCLE_SIZE}px`;
    this.rightFistCircle.style.height = `${GLOW_CIRCLE_SIZE}px`;
    this.rightFistCircle.style.borderRadius = "50%";
    this.rightFistCircle.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
    this.rightFistCircle.style.border = "3px solid rgba(255, 255, 255, 0.9)";
    this.rightFistCircle.style.animation =
      `fist-glow-pulse ${GLOW_PULSE_DURATION} ease-in-out infinite`;
    this.rightFistCircle.style.transform = "translate(-50%, -50%)";
    this.rightFistCircle.style.pointerEvents = "none";
    this.rightPanel.appendChild(this.rightFistCircle);

    // === CREATE DEBUG STATUS OVERLAY ===
    this.debugOverlay = document.createElement("div");
    this.debugOverlay.style.position = "absolute";
    this.debugOverlay.style.top = "20px";
    this.debugOverlay.style.left = "20px";
    this.debugOverlay.style.padding = "12px 16px";
    this.debugOverlay.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
    this.debugOverlay.style.color = "#fff";
    this.debugOverlay.style.fontFamily = "monospace";
    this.debugOverlay.style.fontSize = "14px";
    this.debugOverlay.style.borderRadius = "8px";
    this.debugOverlay.style.zIndex = "1002";
    this.debugOverlay.style.pointerEvents = "none";
    this.debugOverlay.style.opacity = "0";
    this.debugOverlay.style.transition = "opacity 0.2s ease-in";
    this.debugOverlay.textContent = "State: idle";

    // === CREATE SIGNAL INDICATOR ===
    this.signalIndicator = document.createElement("div");
    this.signalIndicator.style.position = "absolute";
    this.signalIndicator.style.top = "50%";
    this.signalIndicator.style.left = "50%";
    this.signalIndicator.style.transform = "translate(-50%, -50%)";
    this.signalIndicator.style.fontSize = "72px";
    this.signalIndicator.style.fontWeight = "bold";
    this.signalIndicator.style.fontFamily =
      "system-ui, -apple-system, sans-serif";
    this.signalIndicator.style.textTransform = "uppercase";
    this.signalIndicator.style.letterSpacing = "8px";
    this.signalIndicator.style.textShadow =
      "0 0 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,255,255,0.4)";
    this.signalIndicator.style.pointerEvents = "none";
    this.signalIndicator.style.zIndex = "1003";
    this.signalIndicator.style.opacity = "0";
    this.signalIndicator.style.transition =
      `opacity ${SIGNAL_FADE_DURATION} ease-out`;
    this.signalIndicator.textContent = "";

    // Assemble DOM
    this.visualizationContainer.appendChild(this.leftPanel);
    this.visualizationContainer.appendChild(this.rightPanel);
    this.localRoot.appendChild(this.visualizationContainer);
    this.localRoot.appendChild(this.debugOverlay);
    this.localRoot.appendChild(this.signalIndicator);

    if (this.options?.enable) {
      parentRoot.appendChild(this.localRoot);
    }
  }

  // Update options and sync DOM visibility. Called from both the metaframe
  // input listener and the hash param listener.
  private applyOptions(options: TwoHandGrabOpenCloseOptions | undefined): void {
    const prev = this.options;
    this.options = options ?? {};

    const parentRoot = this.params.root!;
    if (this.options?.enable) {
      if (this.localRoot.parentElement !== parentRoot) {
        parentRoot.appendChild(this.localRoot);
      }
    } else {
      this.localRoot.remove();
    }

    // Toggle debug overlay independently
    if (this.options?.enable) {
      if (this.options?.debug && !prev?.debug) {
        this.debugOverlay.style.opacity = "1";
      } else if (!this.options?.debug && prev?.debug) {
        this.debugOverlay.style.opacity = "0";
      }
    }
  }

  dispose(): void {
    // console.log(`🗯️🧶🧶 TwoHandGrabOpenCloseController.dispose`);
    this.disposers.forEach((d) => d());
    this.disposers = [];
    this.localRoot.remove();

    if (this.signalFadeTimeout) {
      clearTimeout(this.signalFadeTimeout);
      this.signalFadeTimeout = null;
    }

    if (this.styleSheet) {
      this.styleSheet.remove();
    }
  }

  onInputs(inputs: CombinedResults & { reset?: boolean }): CombinedResults {
    // console.log(
    //   `↗️🧶🧶 TwoHandGrabOpenCloseController.onInputs enabled=${this.options?.enable}`,
    //   inputs,
    // );
    if (!this.options?.enable) {
      return inputs;
    }

    this.handleClench(inputs?.clench);
    // if (inputs.reset) {
    //   this.resetZoom();
    // }
    return inputs;
  }

  // === Metaframe output ===

  private setDetailedOutputs(
    _initialDistance: number | null,
    _currentDistance: number | null,
    zoom: number | null,
    zoomDelta: number | null,
    angle: number | null,
    angleDelta: number | null,
  ): void {
    this.params.iostream.setOutputs({
      zoomTwoHands: zoom,
      zoomTwoHandsDelta: zoomDelta,
      angleTwoHands: angle,
      angleTwoHandsDelta: angleDelta,
    });
  }

  // === Signal indicator ===

  private showSignalIndicator(signal: "OPEN" | "CLOSE"): void {
    if (this.signalFadeTimeout) {
      clearTimeout(this.signalFadeTimeout);
      this.signalFadeTimeout = null;
    }

    this.signalIndicator.textContent = signal;
    if (signal === "OPEN") {
      this.signalIndicator.style.color = "#4ade80";
    } else {
      this.signalIndicator.style.color = "#f87171";
    }

    this.signalIndicator.style.transition = "none";
    this.signalIndicator.style.opacity = "1";

    // Force reflow
    this.signalIndicator.offsetHeight;

    this.signalIndicator.style.transition =
      `opacity ${SIGNAL_FADE_DURATION} ease-out`;

    this.signalFadeTimeout = setTimeout(() => {
      this.signalIndicator.style.opacity = "0";
      this.signalFadeTimeout = null;
    }, SIGNAL_DISPLAY_DURATION_MS);
  }

  // === Debug overlay ===

  private updateDebugOverlay(
    currentState: GestureState,
    warmupElapsed: number | null,
    stillnessOk: boolean,
    normalizedDist: number | null,
  ): void {
    let text = `State: ${currentState}`;

    if (currentState === "warmup") {
      const warmup = this.effectiveWarmupDurationMs;
      const remaining = warmupElapsed !== null
        ? Math.max(0, warmup - warmupElapsed)
        : warmup;
      const progress = warmupElapsed !== null
        ? Math.min(100, (warmupElapsed / warmup) * 100)
        : 0;
      text += `\nWarmup: ${remaining.toFixed(0)}ms remaining`;
      text += `\nProgress: ${progress.toFixed(0)}%`;
      text += `\nStill: ${stillnessOk ? "YES" : "NO (moving)"}`;
      text +=
        `\nSamples: ${this.positionHistory.length}/${MIN_SAMPLES_FOR_STILLNESS}`;
    } else if (
      currentState === "active_opening" ||
      currentState === "active_closing"
    ) {
      text += `\nDistance: ${normalizedDist?.toFixed(2) ?? "?"}`;
      text += `\nMode: ${
        currentState === "active_opening" ? "OPENING" : "CLOSING"
      }`;
    }

    this.debugOverlay.textContent = text;
  }

  // === Stillness detection ===

  private addPositionSample(leftBox: HitBox, rightBox: HitBox): void {
    const sample: PositionSample = {
      leftX: leftBox.x + leftBox.width / 2,
      leftY: leftBox.y + leftBox.height / 2,
      rightX: rightBox.x + rightBox.width / 2,
      rightY: rightBox.y + rightBox.height / 2,
      timestamp: performance.now(),
    };

    this.positionHistory.push(sample);
    if (this.positionHistory.length > POSITION_HISTORY_SIZE) {
      this.positionHistory.shift();
    }
  }

  private clearPositionHistory(): void {
    this.positionHistory.length = 0;
  }

  private isStill(): boolean {
    if (this.positionHistory.length < MIN_SAMPLES_FOR_STILLNESS) return false;

    const leftXVariance = calculateVariance(
      this.positionHistory.map((p) => p.leftX),
    );
    const leftYVariance = calculateVariance(
      this.positionHistory.map((p) => p.leftY),
    );
    const rightXVariance = calculateVariance(
      this.positionHistory.map((p) => p.rightX),
    );
    const rightYVariance = calculateVariance(
      this.positionHistory.map((p) => p.rightY),
    );

    const totalVariance = leftXVariance + leftYVariance + rightXVariance +
      rightYVariance;

    return totalVariance < this.effectiveStillnessThreshold;
  }

  // === Panel positioning ===

  private updateOpeningPanelPositions(
    leftFistX: number,
    rightFistX: number,
  ): void {
    const panelWidth = PANEL_WIDTH_PERCENT / 100;

    // Panels start closed (touching at center = 0.5) and open outward as
    // each hand moves away from its captured initial position.
    // Left panel can only move left (≤ 0.5); right panel can only move right (≥ 0.5).
    const leftDelta = leftFistX - (this.openingInitialLeftX ?? leftFistX);
    const rightDelta = rightFistX - (this.openingInitialRightX ?? rightFistX);

    const leftInnerEdge = Math.min(0.5, 0.5 + leftDelta);
    const rightInnerEdge = Math.max(0.5, 0.5 + rightDelta);

    // Left panel: left=0, width=panelWidth
    // Right edge = panelWidth * (1 + t/100) → set to leftInnerEdge
    const leftTranslatePercent = (leftInnerEdge / panelWidth - 1) * 100;

    // Right panel: right=0, natural left edge = 1 - panelWidth
    // Left edge = (1 - panelWidth) + t/100 * panelWidth → set to rightInnerEdge
    const rightTranslatePercent =
      ((rightInnerEdge - (1 - panelWidth)) / panelWidth) * 100;

    this.leftPanel.style.transform = `translateX(${leftTranslatePercent}%)`;
    this.rightPanel.style.transform = `translateX(${rightTranslatePercent}%)`;
  }

  private updateClosingPanelPositions(
    leftFistX: number,
    rightFistX: number,
  ): void {
    const panelWidth = PANEL_WIDTH_PERCENT / 100;

    // Steel doors: inner edges track the fists but are clamped at center (0.5)
    // so they can never pass each other and overlap.
    const overlap = this.effectivePanelFistOverlap;
    const leftPanelInnerEdge = Math.min(0.5, leftFistX + overlap);
    const rightPanelInnerEdge = Math.max(0.5, rightFistX - overlap);

    const leftEdgeTarget = leftPanelInnerEdge - panelWidth;
    const leftTranslatePercent = (leftEdgeTarget / panelWidth) * 100;

    const rightEdgeTarget = rightPanelInnerEdge + panelWidth;
    const rightTranslatePercent = ((rightEdgeTarget - 1.0) / panelWidth) * 100;

    this.leftPanel.style.transform = `translateX(${leftTranslatePercent}%)`;
    this.rightPanel.style.transform = `translateX(${rightTranslatePercent}%)`;
  }

  private calculateClosingProgress(
    leftFistX: number,
    rightFistX: number,
  ): number {
    if (
      this.closingInitialLeftX === null ||
      this.closingInitialRightX === null
    ) {
      return 0;
    }

    const initialGap = this.closingInitialRightX - this.closingInitialLeftX;
    const currentGap = rightFistX - leftFistX;
    const triggerGap = 2 * this.effectivePanelFistOverlap;

    const totalTravel = initialGap - triggerGap;
    if (totalTravel <= 0) return 1;

    const traveled = initialGap - currentGap;
    return Math.min(1, Math.max(0, traveled / totalTravel));
  }

  private updateFistCirclePositions(leftBox: HitBox, rightBox: HitBox): void {
    const leftCenterX = (leftBox.x + leftBox.width / 2) *
      this.currentViewportWidth;
    const leftCenterY = (leftBox.y + leftBox.height / 2) *
      this.currentViewportHeight;
    const rightCenterX = (rightBox.x + rightBox.width / 2) *
      this.currentViewportWidth;
    const rightCenterY = (rightBox.y + rightBox.height / 2) *
      this.currentViewportHeight;

    const leftPanelRect = this.leftPanel.getBoundingClientRect();
    const rightPanelRect = this.rightPanel.getBoundingClientRect();

    const leftRelativeX = leftCenterX - leftPanelRect.left;
    const leftRelativeY = leftCenterY - leftPanelRect.top;
    this.leftFistCircle.style.left = `${leftRelativeX}px`;
    this.leftFistCircle.style.top = `${leftRelativeY}px`;

    const rightRelativeX = rightCenterX - rightPanelRect.left;
    const rightRelativeY = rightCenterY - rightPanelRect.top;
    this.rightFistCircle.style.left = `${rightRelativeX}px`;
    this.rightFistCircle.style.top = `${rightRelativeY}px`;
  }

  // === State transitions ===

  private transitionToIdle(): void {
    if (this.gestureState !== "idle") {
      const wasActive = this.gestureState === "active_closing" ||
        this.gestureState === "active_opening";

      this.gestureState = "idle";
      this.warmupStartTime = null;
      this.clearPositionHistory();

      this.localRoot.style.opacity = "0";
      this.localRoot.style.visibility = "hidden";
      this.visualizationContainer.style.opacity = "0";
      this.debugOverlay.style.opacity = "0";

      if (wasActive) {
        setTimeout(() => {
          this.leftPanel.style.transform = "translateX(-100%)";
          this.rightPanel.style.transform = "translateX(100%)";
        }, 300);
      } else {
        this.leftPanel.style.transform = "translateX(-100%)";
        this.rightPanel.style.transform = "translateX(100%)";
      }

      this.resetZoomState();
    }
  }

  private resetZoomState(): void {
    this.triggerEventSent = null;
    this.initialDistance = null;
    this.currentDistance = null;
    this.currentZoom = 1.0;
    this.zoomDelta = 0.0;
    this.previousZoom = 1.0;
    this.currentAngle = null;
    this.previousAngle = null;
    this.angleDelta = null;

    this.closingInitialLeftX = null;
    this.closingInitialRightX = null;
    this.closingTriggerDistance = null;

    this.openingInitialLeftX = null;
    this.openingInitialRightX = null;

    this.setDetailedOutputs(
      this.initialDistance,
      this.initialDistance,
      1.0,
      0.0,
      null,
      null,
    );
  }

  private resetZoom(): void {
    this.transitionToIdle();
  }

  // === Main input handler ===

  private get effectiveWarmupDurationMs(): number {
    return this.options?.warmupDurationMs ?? WARMUP_DURATION_MS;
  }

  private get effectiveStillnessThreshold(): number {
    return this.options?.stillnessThreshold ?? STILLNESS_THRESHOLD;
  }

  private get effectiveZoomThresholdTriggerEvent(): number {
    return (
      this.options?.zoomThresholdTriggerEvent ?? ZOOMTHRESHOLD_TRIGGER_EVENT
    );
  }

  private get effectivePanelFistOverlap(): number {
    return this.options?.panelFistOverlap ?? PANEL_FIST_OVERLAP;
  }

  private handleClench(clenchData?: ClenchData): void {
    if (!clenchData) {
      this.transitionToIdle();
      return;
    }

    const { isClenchLeft, isClenchRight, handHitBoxes } = clenchData;

    const isBothClenched = !!isClenchLeft && !!isClenchRight;

    if (!isBothClenched) {
      this.transitionToIdle();
      return;
    }

    const leftBox = handHitBoxes?.[0];
    const rightBox = handHitBoxes?.[1];

    if (!leftBox || !rightBox) {
      this.transitionToIdle();
      return;
    }

    const root = this.params.root;

    // Update viewport dimensions
    const boundingRect = root!.getBoundingClientRect();
    this.currentViewportWidth = boundingRect.width > 0
      ? boundingRect.width
      : window.innerWidth;
    this.currentViewportHeight = boundingRect.height > 0
      ? boundingRect.height
      : window.innerHeight;

    // Add position sample for stillness detection
    this.addPositionSample(leftBox, rightBox);

    // Calculate screen coordinates for zoom/angle outputs
    const leftBoxScreen = {
      x: leftBox.x * this.currentViewportWidth,
      y: leftBox.y * this.currentViewportHeight,
      width: leftBox.width * this.currentViewportWidth,
      height: leftBox.height * this.currentViewportHeight,
    };
    const rightBoxScreen = {
      x: rightBox.x * this.currentViewportWidth,
      y: rightBox.y * this.currentViewportHeight,
      width: rightBox.width * this.currentViewportWidth,
      height: rightBox.height * this.currentViewportHeight,
    };

    // Calculate angle
    const leftCenter = {
      x: leftBoxScreen.x + leftBoxScreen.width / 2,
      y: leftBoxScreen.y + leftBoxScreen.height / 2,
    };
    const rightCenter = {
      x: rightBoxScreen.x + rightBoxScreen.width / 2,
      y: rightBoxScreen.y + rightBoxScreen.height / 2,
    };
    const angleRadians = Math.atan2(
      rightCenter.y - leftCenter.y,
      rightCenter.x - leftCenter.x,
    );

    // Calculate current distance
    const calculatedDistance = calculateCenterDistance(
      leftBoxScreen,
      rightBoxScreen,
    );
    this.currentDistance = calculatedDistance;

    // State machine logic
    switch (this.gestureState) {
      case "idle":
        this.gestureState = "warmup";
        this.warmupStartTime = performance.now();

        this.localRoot.style.opacity = "1";
        this.localRoot.style.visibility = "visible";
        if (this.options?.debug) {
          this.debugOverlay.style.opacity = "1";
        }
        this.updateDebugOverlay("warmup", 0, false, null);
        break;

      case "warmup": {
        const stillnessOk = this.isStill();
        const elapsed = performance.now() - (this.warmupStartTime ?? 0);

        if (!stillnessOk) {
          this.warmupStartTime = performance.now();
          this.updateDebugOverlay("warmup", 0, false, null);
        } else {
          this.updateDebugOverlay("warmup", elapsed, true, null);

          if (elapsed >= this.effectiveWarmupDurationMs) {
            const normalizedDistance = calculateNormalizedFistDistance(
              leftBox,
              rightBox,
            );

            const leftFistX = leftBox.x + leftBox.width / 2;
            const rightFistX = rightBox.x + rightBox.width / 2;

            if (normalizedDistance > 0.5) {
              this.gestureState = "active_closing";

              this.closingInitialLeftX = leftFistX;
              this.closingInitialRightX = rightFistX;
              this.closingTriggerDistance = 2 * this.effectivePanelFistOverlap;
            } else {
              this.gestureState = "active_opening";
              this.openingInitialLeftX = leftFistX;
              this.openingInitialRightX = rightFistX;
              this.closingInitialLeftX = null;
              this.closingInitialRightX = null;
              this.closingTriggerDistance = null;
            }

            // Initialize zoom tracking
            this.initialDistance = calculatedDistance;
            this.currentZoom = 0;
            this.zoomDelta = 0.0;
            this.previousZoom = 0;
            this.currentAngle = angleRadians;
            this.previousAngle = angleRadians;
            this.angleDelta = 0.0;

            // Set panel positions BEFORE showing (no animation on appear)
            this.leftPanel.style.transition = "none";
            this.rightPanel.style.transition = "none";

            if (this.gestureState === "active_closing") {
              this.updateClosingPanelPositions(leftFistX, rightFistX);
            } else {
              this.updateOpeningPanelPositions(leftFistX, rightFistX);
            }
            this.updateFistCirclePositions(leftBox, rightBox);

            // Force reflow
            this.leftPanel.offsetHeight;
            this.rightPanel.offsetHeight;

            // Re-enable transitions
            this.leftPanel.style.transition =
              `transform ${PANEL_TRANSITION_DURATION} ${PANEL_TRANSITION_EASING}`;
            this.rightPanel.style.transition =
              `transform ${PANEL_TRANSITION_DURATION} ${PANEL_TRANSITION_EASING}`;

            this.visualizationContainer.style.opacity = "1";
            this.updateDebugOverlay(
              this.gestureState,
              null,
              true,
              normalizedDistance,
            );

            this.setDetailedOutputs(
              this.initialDistance,
              calculatedDistance,
              0.0,
              0.0,
              angleRadians,
              0.0,
            );
          }
        }
        break;
      }

      case "active_closing":
      case "active_opening": {
        // State is locked at activation — opening stays opening, closing stays closing.
        // This ensures panels only travel one direction and only the matching event fires.
        const leftFistX = leftBox.x + leftBox.width / 2;
        const rightFistX = rightBox.x + rightBox.width / 2;

        // Update debug overlay
        if (this.options?.debug) {
          if (this.gestureState === "active_closing") {
            const progress = this.calculateClosingProgress(
              leftFistX,
              rightFistX,
            );
            const currentGap = rightFistX - leftFistX;
            this.debugOverlay.textContent =
              `State: ${this.gestureState}\nGap: ${
                (
                  currentGap * 100
                ).toFixed(1)
              }%\nProgress: ${
                (progress * 100).toFixed(
                  0,
                )
              }%\nTrigger at: ${
                (
                  2 *
                  this.effectivePanelFistOverlap *
                  100
                ).toFixed(1)
              }%`;
          } else {
            this.updateDebugOverlay(
              this.gestureState,
              null,
              true,
              calculateNormalizedFistDistance(leftBox, rightBox),
            );
          }
        }

        // Update panel positions based on mode
        if (this.gestureState === "active_closing") {
          this.updateClosingPanelPositions(leftFistX, rightFistX);

          if (
            shouldTriggerClose(
              leftFistX,
              rightFistX,
              this.effectivePanelFistOverlap,
            )
          ) {
            if (!this.triggerEventSent || this.triggerEventSent !== -1) {
              this.triggerEventSent = -1;
              this.params.iostream.setOutputs({
                zoomThresholdEvent: -1,
              });
              this.showSignalIndicator("CLOSE");
            }
          }
        } else {
          this.updateOpeningPanelPositions(leftFistX, rightFistX);
        }

        // Calculate zoom factor
        if (this.initialDistance !== null && this.initialDistance > 0) {
          const newZoom = Math.log(calculatedDistance / this.initialDistance);
          this.zoomDelta = newZoom - this.previousZoom;
          this.currentZoom = newZoom;

          if (this.previousAngle !== null) {
            this.angleDelta = normalizeAngleDelta(
              this.previousAngle,
              angleRadians,
            );
          } else {
            this.angleDelta = 0.0;
          }
          this.currentAngle = angleRadians;

          this.setDetailedOutputs(
            this.initialDistance,
            calculatedDistance,
            this.currentZoom,
            this.zoomDelta,
            this.currentAngle,
            this.angleDelta,
          );

          // Check for threshold trigger (for opening mode only — panels start closed
          // so only an OPEN event can fire; hands spreading inward cannot close)
          if (this.gestureState === "active_opening") {
            if (
              !this.triggerEventSent &&
              this.currentZoom > this.effectiveZoomThresholdTriggerEvent
            ) {
              this.triggerEventSent = 1;
              this.params.iostream.setOutputs({
                zoomThresholdEvent: 1,
              });
              this.showSignalIndicator("OPEN");
            }
          }

          this.previousZoom = this.currentZoom;
          this.previousAngle = this.currentAngle;
        }
        break;
      }
    }
  }
}
